import {
  CreateFlexibleReportConfigurationResponse,
  GetFlexibleReportConfigurationResponse
} from '@flexible-reports/store/api/subscriptions/api-types';
import { CreateApiErrorResponse, CreateApiSuccessResponse } from '@flexible-reports/pages/create-subscription/create-button/types';
import { FlexibleSubscription, FlexibleReportPeriod, ReportDelivery, ReportSchedule } from '@flexible-reports/store/slices/types';
import {
  DatePeriod,
  FutureSchedule,
  RecurringMonthlyPresetSchedule,
  RecurringMonthlySchedule,
  RecurringSchedule,
  RecurringWeeklySchedule,
  ReportConfigurationSchedule,
  ReportDeliverySettings,
  ReportPeriod,
  ReportPresetPeriod,
  ScheduleType
} from '@flexible-reports/store/api/subscriptions/types';
import { FREQUENCY, ONCE_SCHEDULE_DATE_OPTION } from '@flexible-reports/utils/schedule-constants';
import { periodPresetFromApiMap } from '@flexible-reports/store/api/subscriptions/transformers/mappings';
import { transformTimezoneFromApi } from '@flexible-reports/store/api/subscriptions/transformers/timezoneTransformer';
import { PRESET_PERIOD } from '@flexible-reports/components/date-input/utils/constants';
import { UpdateApiErrorResponse } from '@flexible-reports/pages/view-subscription/report-settings/update-button/types';
import { CompositePredicate, Filter } from '@flexible-reports/store/api/types';
import { shouldExcludeManagerAccounts } from '@flexible-reports/utils/app-utils';
import { determineCountryCodesFromResponse } from '@flexible-reports/utils/country-utils';

/**
 * This function handles response transformation for Create Report.
 * It assumes that only a single report is created and the response includes data for only the single created report.
 * @param response - Response from ROSS Create Flexible Report API
 */
export const transformCreateReportSuccessResponse = (response: CreateFlexibleReportConfigurationResponse): CreateApiSuccessResponse => {
  if (!response) {
    throw new Error('Response from Create Flexible Report API is undefined');
  }
  if (!response.success || (response.success && response.success.length !== 1)) {
    throw new Error(`Invalid Success Response from Create Subscription API ${response}`);
  }
  return { reportConfigurationId: response.success[0].reportConfiguration?.reportConfigurationId };
};

export const transformCreateReportErrorResponse = (error: any): CreateApiErrorResponse => {
  return {
    status: error?.status as number,
    errorCode: error?.data?.code,
    message: error?.data?.message
  };
};

export const transformUpdateReportErrorResponse = (error: any): UpdateApiErrorResponse => {
  return {
    status: error?.status as number,
    errorCode: error?.data?.code,
    message: error?.data?.message
  };
};

/**
 * This function handles response transformation for Get Report By ID.
 * The current UI does not have a use case to get Multiple Report Configurations By ID.
 * The RTK Query hook respectively only supports passing in a single report configuration ID.
 * The logic for this function assumes that only a single report configuration is returned from the API
 * @param response
 */
export const transformGetFlexibleReportByIdResponse = (response: GetFlexibleReportConfigurationResponse): FlexibleSubscription => {
  if (!response || !response.success || response.success.length !== 1) {
    throw new Error(`Invalid Success Response from Get Flexible Report By Id API ${response}`);
  }
  const reportConfiguration = response.success[0].reportConfiguration;

  const countryFilter = getFilterForField(reportConfiguration.query.filter, 'campaign.country');
  const countries = determineCountryCodesFromResponse(countryFilter?.on?.values || []);

  const campaignFilter = getFilterForField(reportConfiguration.query.filter, 'campaign.id');
  const campaignIds = campaignFilter?.on?.values || [];
  const useAllCampaigns = campaignIds.length === 0;

  const productTypeFilter = getFilterForField(reportConfiguration.query.filter, 'adProduct.value');
  const productTypes = productTypeFilter?.on?.values || [];

  return {
    reportConfigurationId: reportConfiguration.reportConfigurationId,
    reportName: reportConfiguration.name,
    reportFilters: {
      managerAccountIds: shouldExcludeManagerAccounts()
        ? []
        : ((reportConfiguration.linkedAccounts ?? [])
            .filter(account => account.managerAccountId)
            .map(account => account.managerAccountId) as string[]),
      advertiserAccountIds: (reportConfiguration.linkedAccounts ?? [])
        .filter(account => account.advertiserAccountId)
        .map(account => account.advertiserAccountId) as string[],
      countries: countries,
      campaignIds: campaignIds,
      useAllCampaigns: useAllCampaigns,
      productTypes: productTypes
    },
    reportFields: reportConfiguration.query.fields,
    reportFormat: reportConfiguration.format,
    reportPeriod: transformFromApiReportPeriod(reportConfiguration.period, reportConfiguration.timeZoneMode),
    reportDelivery: transformFromApiReportDelivery(reportConfiguration.deliverySettings),
    reportSchedule: transformFromApiReportSchedule(reportConfiguration.scheduleType, reportConfiguration.schedule),
    reportFailureCode: reportConfiguration.latestScheduledReportFailureCode,
    latestReportId: reportConfiguration.latestScheduledReportId,
    reportStatus: reportConfiguration.status,
    linkedAccounts: reportConfiguration.linkedAccounts,
    columnSettings: {
      selectedCurrencyOfView: reportConfiguration.currencyOfView
    }
  };
};

const getFilterForField = (filter: Filter | undefined, field: string): Filter | undefined => {
  if (!filter) {
    return;
  }

  if (filter.and) {
    return getFilterFromAnd(filter.and, field);
  }

  if (filter.on) {
    return getFromFromOn(filter, field);
  }

  return;
};

const getFilterFromAnd = (and: CompositePredicate, field: string): Filter | undefined => {
  return and.filters?.find(filter => filter.on?.field === field);
};

const getFromFromOn = (filter: Filter, field: string): Filter | undefined => {
  return filter.on?.field === field ? filter : undefined;
};

const transformFromApiReportSchedule = (scheduleType: string, schedule: ReportConfigurationSchedule): ReportSchedule => {
  if (!scheduleType) {
    throw new Error(`Schedule is Missing in API Response`);
  }

  if (scheduleType === ScheduleType.NOW) {
    return {
      frequency: FREQUENCY.ONCE,
      onceDateOption: ONCE_SCHEDULE_DATE_OPTION.TODAY
    };
  }
  if (scheduleType === ScheduleType.FUTURE) {
    return transformFromFutureApiSchedule(schedule.futureSchedule);
  }
  if (scheduleType === ScheduleType.RECURRING_DAILY) {
    return transformFromDailyApiSchedule(schedule.recurringDailySchedule);
  }
  if (scheduleType === ScheduleType.RECURRING_WEEKLY) {
    return transformFromWeeklyApiSchedule(schedule.recurringWeeklySchedule);
  }
  if (scheduleType === ScheduleType.RECURRING_MONTHLY) {
    return transformFromMonthlyApiSchedule(schedule.recurringMonthlySchedule, schedule.recurringMonthlyPresetSchedule);
  }
  throw new Error(`Invalid Schedule Type ${scheduleType}`);
};

const transformFromFutureApiSchedule = (futureSchedule?: FutureSchedule): ReportSchedule => {
  if (!futureSchedule) {
    throw new Error(`Future Schedule is Missing in API Response`);
  }
  return {
    frequency: FREQUENCY.ONCE,
    onceDateOption: ONCE_SCHEDULE_DATE_OPTION.FUTURE,
    runDate: futureSchedule.runDate,
    timezone: transformTimezoneFromApi(futureSchedule.timeZone),
    hour: futureSchedule.hourOfDay
  };
};

const transformFromDailyApiSchedule = (dailySchedule?: RecurringSchedule): ReportSchedule => {
  if (!dailySchedule) {
    throw new Error(`Recurring Daily Schedule is Missing in API Response`);
  }
  return {
    frequency: FREQUENCY.DAILY,
    endDate: dailySchedule.expirationDate,
    timezone: transformTimezoneFromApi(dailySchedule.timeZone),
    hour: dailySchedule.hourOfDay
  };
};

const transformFromWeeklyApiSchedule = (weeklySchedule?: RecurringWeeklySchedule): ReportSchedule => {
  if (!weeklySchedule) {
    throw new Error(`Recurring Weekly Schedule is Missing in API Response`);
  }
  return {
    frequency: FREQUENCY.WEEKLY,
    endDate: weeklySchedule.expirationDate,
    timezone: transformTimezoneFromApi(weeklySchedule.timeZone),
    hour: weeklySchedule.hourOfDay,
    daysOfWeek: weeklySchedule.daysOfWeek
  };
};

const transformFromMonthlyApiSchedule = (
  monthlySchedule?: RecurringMonthlySchedule,
  monthlyPresetSchedule?: RecurringMonthlyPresetSchedule
): ReportSchedule => {
  if (!monthlySchedule && !monthlyPresetSchedule) {
    throw new Error(`Recurring Monthly Schedule is Missing in API Response`);
  }

  if (monthlySchedule) {
    return {
      frequency: FREQUENCY.MONTHLY,
      endDate: monthlySchedule.expirationDate,
      timezone: transformTimezoneFromApi(monthlySchedule.timeZone),
      hour: monthlySchedule.hourOfDay,
      dayOfMonth: monthlySchedule.dayOfMonth
    };
  }
  if (monthlyPresetSchedule) {
    return {
      frequency: FREQUENCY.MONTHLY,
      endDate: monthlyPresetSchedule.expirationDate,
      timezone: transformTimezoneFromApi(monthlyPresetSchedule.timeZone),
      hour: monthlyPresetSchedule.hourOfDay,
      lastDayOfMonth: true
    };
  }
  // Unreachable Code.
  throw new Error(`Invalid Monthly Schedule Encountered in API Response`);
};

const transformFromApiReportDelivery = (deliverySettings: ReportDeliverySettings): ReportDelivery => {
  return {
    emails: deliverySettings?.email ? deliverySettings.email : []
  };
};

const transformFromApiReportPeriod = (reportPeriod: ReportPeriod, timezoneMode?: string): FlexibleReportPeriod => {
  if (!reportPeriod) {
    throw new Error(`Report Period is Missing in API Response`);
  }
  if (!reportPeriod.datePeriod && !reportPeriod.reportConfigurationPresetPeriod) {
    throw new Error(`Report Period Date Period and Preset Period both are Missing in API Response`);
  }
  if (reportPeriod.datePeriod) {
    return transformCustomReportPeriod(reportPeriod.datePeriod, timezoneMode);
  }
  if (reportPeriod.reportConfigurationPresetPeriod) {
    return transformPresetReportPeriod(reportPeriod.reportConfigurationPresetPeriod, timezoneMode);
  }
  //Unreachable code.
  throw new Error(`Invalid Report Period Configuration`);
};

const transformCustomReportPeriod = (datePeriod: DatePeriod, timezoneMode?: string): FlexibleReportPeriod => {
  return {
    preset: PRESET_PERIOD.CUSTOM,
    start: datePeriod.startDate,
    end: datePeriod.endDate,
    ...(timezoneMode && { reportingTimezone: timezoneMode })
  };
};

const transformPresetReportPeriod = (presetPeriod: ReportPresetPeriod, timezoneMode?: string): FlexibleReportPeriod => {
  const preset = periodPresetFromApiMap[presetPeriod.preset] || presetPeriod.preset;
  if (!(preset in PRESET_PERIOD)) {
    throw new Error('Invalid Report Period Preset returned in API Response');
  }
  return {
    preset: preset,
    ...(timezoneMode && { reportingTimezone: timezoneMode })
  };
};
